import React, { useRef, useEffect } from 'react';
import { useIntl, defineMessages } from 'react-intl';

import { cssMap, cx } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip/Tooltip';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Pressable } from '@atlaskit/primitives/compiled/pressable';
import { Inline } from '@atlaskit/primitives/compiled/inline';

import { AddCommentPromptButton } from '@confluence/comments-common/entry-points/AddCommentPromptButton.ts';
import { useAddCommentPermissionCheck } from '@confluence/comments-hooks/entry-points/useAddCommentPermissionCheck.ts';
import { expValEquals } from '@confluence/feature-experiments';
import { usePageContentId } from '@confluence/page-context';
import { useShortcutStrings } from '@confluence/shortcuts/entry-points/ShortcutVisualizer.ts';
import { UNIVERSAL_OPEN_COMMENT_EDITOR } from '@confluence/shortcuts/entry-points/confluence-shortcuts.ts';

const styles = cssMap({
	inputStyle: {
		display: 'flex',
		minHeight: '32px',
		paddingLeft: token('space.150'),
		paddingRight: token('space.150'),
		justifyContent: 'flex-start',
		alignItems: 'center',
		width: '100%',
		borderRadius: token('radius.large'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
		backgroundColor: token('elevation.surface'),
		font: token('font.body'),
		color: token('color.text.subtlest'),
	},
	enabledStyle: {
		'&:hover': {
			cursor: 'text',
			backgroundColor: token('elevation.surface.hovered'),
		},
	},
	disabledStyle: {
		opacity: token('opacity.disabled'),
		cursor: 'not-allowed',
	},
});

const i18n = defineMessages({
	addCommentTooltip: {
		id: 'object-comments.footer.add-button-tooltip',
		defaultMessage: 'Add comment',
		description:
			'Tooltip message for adding a comment, used in conjunction with shortcut visualizer',
	},
	addCommentPromptText: {
		id: 'object-comments.add-comment-prompt.text',
		defaultMessage: 'Add a comment',
		description:
			'The text is shown on a button that prompts the user to add a comment to the page or content item.',
	},
	addCommentPromptAriaLabel: {
		id: 'object-comments.add-comment-prompt.aria-label',
		defaultMessage: 'Add a general comment',
		description: 'The aria label for the add general comment entry prompt field',
	},
});

type ObjectAddCommentPromptProps = {
	onClick: () => void;
	autofocusRef: { current: boolean | null };
};

// New footer's collapsed trigger: styled as a single-line text input and activates on `onFocus`
// rather than `onClick`, so a disabled native button never receives focus and `canAddComments`
// gating falls out for free instead of needing a manual guard.
const NewAddCommentPrompt = ({
	onClick,
	autofocusRef,
}: ObjectAddCommentPromptProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const [contentId] = usePageContentId();
	const { canAddComments } = useAddCommentPermissionCheck(contentId || '');
	const buttonRef = useRef<HTMLButtonElement>(null);

	useEffect(() => {
		if (autofocusRef.current) {
			buttonRef.current?.focus({
				preventScroll: true,
			});
			autofocusRef.current = false;
		}
	}, [autofocusRef]);

	return (
		<Pressable
			xcss={cx(styles.inputStyle, !canAddComments ? styles.disabledStyle : styles.enabledStyle)}
			isDisabled={!canAddComments}
			onFocus={onClick}
			testId="add-comment-button"
			ref={buttonRef}
			aria-label={formatMessage(i18n.addCommentPromptAriaLabel)}
		>
			<Inline>{formatMessage(i18n.addCommentPromptText)}</Inline>
		</Pressable>
	);
};

export const ObjectAddCommentPrompt = ({
	onClick,
	autofocusRef,
}: ObjectAddCommentPromptProps): React.JSX.Element => {
	const { formatMessage } = useIntl();

	const addCommentShortcutStrings = useShortcutStrings({
		shortcut: UNIVERSAL_OPEN_COMMENT_EDITOR,
	});

	const buttonRef = useRef<HTMLButtonElement>(null);

	useEffect(() => {
		if (autofocusRef.current) {
			(buttonRef.current as HTMLElement)?.focus({
				preventScroll: true,
			});
			autofocusRef.current = false;
		}
	}, [autofocusRef]);

	if (expValEquals('confluence_footer_reactions_redesign', 'isEnabled', true)) {
		return <NewAddCommentPrompt onClick={onClick} autofocusRef={autofocusRef} />;
	}

	return (
		<Box>
			<Tooltip
				content={
					expValEquals('confluence_comments_cohiti_footer_tooltips', 'isEnabled', true)
						? formatMessage(i18n.addCommentTooltip)
						: undefined
				}
				shortcut={addCommentShortcutStrings}
				position="top"
			>
				<AddCommentPromptButton
					onClick={onClick}
					autofocusRef={autofocusRef}
					testId="add-comment-button"
				/>
			</Tooltip>
		</Box>
	);
};
